import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Star, MessageSquare, Flag, Check, X, Pencil, History } from "lucide-react";
import { toast } from "sonner";
import { T } from "./pdUtil";
import { listComments, addComment, editComment, setReviewFlag, setWatchlist, chartActivity } from "@/lib/patientDocs";

export const REVIEW_FLAGS = [
  ["additional_review", "Additional review", "#9A5A00", "#FFF3E0", "#F0D9B5"],
  ["abnormal", "Abnormal", "#C4302A", "#FCEBEA", "#F2CFCD"],
  ["normal", "Normal", "#1F7A43", "#E7F6EC", "#CCE9D6"],
];
export const flagMeta = (v) => REVIEW_FLAGS.find((f) => f[0] === v) || null;

// A small popover that portals to <body> and anchors below-right of its trigger,
// so it never clips inside the scrollable list grid.
export function Anchored({ anchorRef, onClose, width = 340, align = "right", children }) {
  const [pos, setPos] = useState(null);
  const panelRef = useRef(null);
  const place = React.useCallback(() => {
    const r = anchorRef.current?.getBoundingClientRect();
    if (!r) return;
    const w = Math.min(width, window.innerWidth - 24);
    // content area = region right of the side menu (the <nav>), so the menu can never cross into it.
    const navEl = document.querySelector("nav.rq-sidenav");
    const minLeft = Math.max(8, (navEl ? navEl.getBoundingClientRect().right : 0) + 8);
    let left = align === "left" ? r.left - 2 : r.right - w;
    left = Math.max(minLeft, Math.min(left, window.innerWidth - w - 8));
    setPos({ top: r.bottom + 6, left, w });
  }, [anchorRef, width, align]);
  useLayoutEffect(() => { place(); }, [place]);
  useEffect(() => {
    const onDoc = (e) => { if (!panelRef.current?.contains(e.target) && !anchorRef.current?.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const onResize = () => place();
    const onScroll = (e) => { if (panelRef.current && panelRef.current.contains(e.target)) return; onClose(); };
    document.addEventListener("mousedown", onDoc); document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize); window.addEventListener("scroll", onScroll, true);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); window.removeEventListener("resize", onResize); window.removeEventListener("scroll", onScroll, true); };
  }, [anchorRef, onClose, place]);
  if (!pos) return null;
  return createPortal(
    <div ref={panelRef} className="pd-scope" role="dialog" aria-modal="false"
      style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.w, zIndex: 80, background: T.card, border: `1px solid ${T.hair}`, borderRadius: 12, boxShadow: T.shadow, maxHeight: "min(70vh, 560px)", display: "flex", flexDirection: "column", overflow: "hidden" }}
      onClick={(e) => e.stopPropagation()}>
      {children}
    </div>, document.body);
}

const whenStr = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso || ""; } };

/* ─── Watchlist star ─── */
export function WatchStar({ analysisId, on, onPatch, size = 15 }) {
  const [busy, setBusy] = useState(false);
  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return; setBusy(true);
    const next = !on;
    onPatch?.({ edi_watchlist: next });               // optimistic
    try { await setWatchlist(analysisId, next); }
    catch { onPatch?.({ edi_watchlist: on }); toast.error("Couldn't update watchlist"); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={toggle} data-testid={`pd-watch-${analysisId}`} aria-pressed={!!on}
      title={on ? "On watchlist — click to release" : "Add to watchlist"}
      className="inline-flex items-center justify-center flex-none rounded-[6px] hover:bg-black/[.05]" style={{ width: 24, height: 24 }}>
      <Star size={size} strokeWidth={1.8} style={{ color: on ? "#E8A317" : T.mute, fill: on ? "#F5B800" : "none" }} />
    </button>
  );
}

/* ─── Review-flag chip + menu ─── */
export function FlagChip({ analysisId, flag, onPatch }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const m = flagMeta(flag);
  const choose = async (v, e) => {
    e.stopPropagation(); setOpen(false);
    const prev = flag;
    onPatch?.({ edi_review_flag: v });                // optimistic
    try { await setReviewFlag(analysisId, v); }
    catch { onPatch?.({ edi_review_flag: prev }); toast.error("Couldn't set flag"); }
  };
  return (
    <>
      <button type="button" ref={btnRef} data-testid={`pd-flag-${analysisId}`} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={m ? `Review flag: ${m[1]}` : "Set a review flag"}
        className="inline-flex items-center gap-1 flex-none rounded-full font-semibold hover:brightness-[.98]"
        style={m
          ? { fontSize: 10.5, padding: "2px 8px", color: m[2], background: m[3], border: `1px solid ${m[4]}` }
          : { fontSize: 10.5, padding: "2px 7px", color: T.mute, background: "transparent", border: `1px dashed ${T.hair}` }}>
        <Flag size={11} strokeWidth={2} style={{ color: m ? m[2] : T.mute, fill: m ? m[2] : "none" }} />{m ? m[1] : "Flag"}
      </button>
      {open ? (
        <Anchored anchorRef={btnRef} width={220} onClose={() => setOpen(false)}>
          <div style={{ padding: 6 }}>
            {REVIEW_FLAGS.map(([v, label, c, bg, bd]) => (
              <button key={v} type="button" data-testid={`pd-flag-opt-${v}-${analysisId}`} onClick={(e) => choose(v, e)}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-[8px] text-[13px] hover:bg-black/[.04]" style={{ color: T.ink }}>
                <span className="inline-flex items-center justify-center rounded-full" style={{ width: 16, height: 16, background: bg, border: `1px solid ${bd}` }}><Flag size={9} style={{ color: c, fill: c }} /></span>
                <span className="flex-1 text-left">{label}</span>
                {flag === v ? <Check size={14} style={{ color: T.indigo }} /> : null}
              </button>
            ))}
            {flag ? <button type="button" data-testid={`pd-flag-clear-${analysisId}`} onClick={(e) => choose(null, e)}
              className="w-full flex items-center gap-2 px-2.5 py-2 rounded-[8px] text-[13px] hover:bg-black/[.04]" style={{ color: T.sub, borderTop: `1px solid ${T.soft}`, marginTop: 4 }}>
              <X size={13} style={{ color: T.mute }} /><span className="flex-1 text-left">Clear flag</span></button> : null}
          </div>
        </Anchored>
      ) : null}
    </>
  );
}

/* ─── Comment thread (list + add + edit + change log) ─── */
function CommentItem({ c }) {
  const [log, setLog] = useState(false);
  const who = c.author?.name || c.author?.email || c.author?.login_id || "Someone";
  const hist = Array.isArray(c.history) ? c.history : [];
  return (
    <div style={{ padding: "10px 12px", borderTop: `1px solid ${T.soft}` }} data-testid={`pd-comment-${c.comment_id}`}>
      <div className="text-[13px] whitespace-pre-wrap break-words" style={{ color: T.ink }}>{c.text}</div>
      <div className="flex items-center gap-1.5 mt-1 text-[11px]" style={{ color: T.faint }}>
        <span className="font-semibold" style={{ color: T.sub }}>{who}</span>
        <span>· {whenStr(c.updated_at || c.created_at)}</span>
        {c.edited ? <span className="px-1 rounded" style={{ background: T.soft }}>edited</span> : null}
        {hist.length > 1 ? <button type="button" onClick={() => setLog((v) => !v)} className="inline-flex items-center gap-1 hover:underline" style={{ color: T.indigo }} data-testid={`pd-comment-log-${c.comment_id}`}><History size={11} />{log ? "Hide" : "Change log"}</button> : null}
      </div>
      {log ? (
        <div className="mt-1.5 pl-2.5 flex flex-col gap-1" style={{ borderLeft: `2px solid ${T.hair}` }}>
          {hist.map((h, i) => (
            <div key={i} className="text-[11.5px]" style={{ color: T.sub }}>
              <span className="font-semibold capitalize">{h.action}</span> · {h.by?.name || "—"} · {whenStr(h.at)}
              <div className="whitespace-pre-wrap break-words" style={{ color: T.faint }}>{h.text}</div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function CommentsThread({ analysisId, onPatch, editingComment, setEditingComment }) {
  const [state, setState] = useState({ loading: true, comments: [] });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try { const d = await listComments(analysisId); setState({ loading: false, comments: d.comments || [] }); }
    catch { setState({ loading: false, comments: [] }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [analysisId]);
  const submit = async () => {
    const t = text.trim(); if (!t || busy) return; setBusy(true);
    try {
      await addComment(analysisId, t); setText("");
      onPatch?.({ edi_comment_count: (state.comments.length || 0) + 1 });
      await load();
    } catch (e) { toast.error(e?.response?.status === 400 ? "Comment can't be empty or too long" : "Couldn't add comment"); }
    finally { setBusy(false); }
  };
  const saveEdit = async (cid) => {
    const t = editText.trim(); if (!t || busy) return; setBusy(true);
    try { await editComment(analysisId, cid, t); setEditId(null); setEditText(""); await load(); }
    catch { toast.error("Couldn't save edit"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <div style={{ overflowY: "auto", flex: 1 }} data-testid="pd-comments-list">
        {state.loading ? <div className="px-3 py-4 text-[12.5px]" style={{ color: T.faint }}>Loading comments…</div>
          : !state.comments.length ? <div className="px-3 py-5 text-[12.5px] text-center" style={{ color: T.faint }}>No comments yet. Add the first note below.</div>
          : state.comments.map((c) => (
            editId === c.comment_id ? (
              <div key={c.comment_id} style={{ padding: "10px 12px", borderTop: `1px solid ${T.soft}` }}>
                <textarea autoFocus value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} data-testid={`pd-comment-edit-input-${c.comment_id}`}
                  className="w-full text-[13px] rounded-[8px] p-2" style={{ border: `1px solid ${T.hair}`, resize: "vertical" }} />
                <div className="flex items-center gap-2 mt-1.5">
                  <button type="button" className="pd-btn" style={{ minHeight: 28, padding: "3px 10px" }} disabled={busy} onClick={() => saveEdit(c.comment_id)} data-testid={`pd-comment-save-${c.comment_id}`}>Save</button>
                  <button type="button" className="pd-btn txt" style={{ minHeight: 28 }} onClick={() => { setEditId(null); setEditText(""); }}>Cancel</button>
                  <span className="text-[11px]" style={{ color: T.faint }}>Previous text is kept in the change log.</span>
                </div>
              </div>
            ) : (
              <div key={c.comment_id} className="relative group">
                <CommentItem c={c} />
                <button type="button" onClick={() => { setEditId(c.comment_id); setEditText(c.text); }} title="Edit" data-testid={`pd-comment-editbtn-${c.comment_id}`}
                  className="absolute top-2 right-2 inline-flex items-center justify-center rounded-[6px] opacity-0 group-hover:opacity-100 hover:bg-black/[.05]" style={{ width: 24, height: 24 }}>
                  <Pencil size={13} style={{ color: T.mute }} />
                </button>
              </div>
            )
          ))}
      </div>
      <div style={{ padding: 10, borderTop: `1px solid ${T.hair}` }}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Add a comment…" data-testid="pd-comment-input"
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
          className="w-full text-[13px] rounded-[8px] p-2" style={{ border: `1px solid ${T.hair}`, resize: "vertical" }} />
        <div className="flex items-center gap-2 mt-1.5">
          <button type="button" className="pd-btn pri" style={{ minHeight: 30 }} disabled={busy || !text.trim()} onClick={submit} data-testid="pd-comment-add">Add comment</button>
          <span className="text-[11px]" style={{ color: T.faint }}>⌘/Ctrl + Enter · comments are never deleted</span>
        </div>
      </div>
    </>
  );
}

// Comment badge that opens the thread in a popover (used on list rows AND the open-doc toolbar).
export function CommentButton({ analysisId, count = 0, onPatch, label }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" ref={btnRef} data-testid={`pd-comments-btn-${analysisId}`} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={count ? `${count} comment${count === 1 ? "" : "s"}` : "Add a comment"}
        className="inline-flex items-center gap-1 flex-none rounded-[6px] hover:bg-black/[.05]" style={{ padding: "2px 6px", height: 24, color: count ? T.indigo : T.mute }}>
        <MessageSquare size={14} strokeWidth={1.9} />
        {count ? <span className="text-[11.5px] font-bold tabular-nums">{count}</span> : (label ? <span className="text-[12px] font-semibold">{label}</span> : null)}
      </button>
      {open ? (
        <Anchored anchorRef={btnRef} width={360} onClose={() => setOpen(false)}>
          <div className="flex items-center gap-2 px-3 py-2.5" style={{ borderBottom: `1px solid ${T.hair}` }}>
            <MessageSquare size={14} style={{ color: T.indigo }} />
            <span className="text-[13px] font-bold" style={{ color: T.ink }}>Comments</span>
            <span className="flex-1" />
            <button type="button" onClick={() => setOpen(false)} className="inline-flex items-center justify-center rounded-[6px] hover:bg-black/[.05]" style={{ width: 24, height: 24 }} data-testid="pd-comments-close"><X size={14} style={{ color: T.mute }} /></button>
          </div>
          <CommentsThread analysisId={analysisId} onPatch={onPatch} />
        </Anchored>
      ) : null}
    </>
  );
}

// Header flag (§2): icon + flag name, no pill and no amber background.
// Abnormal / Additional review render the name in amber 600; Normal in grey.
export function HeaderFlag({ analysisId, flag, onPatch }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const m = flagMeta(flag);
  const choose = async (v, e) => {
    e.stopPropagation(); setOpen(false);
    const prev = flag;
    onPatch?.({ edi_review_flag: v, edi_review_flag_at: new Date().toISOString() });
    try { await setReviewFlag(analysisId, v); toast.message(v ? `Flagged ${flagMeta(v)[1]}` : "Flag cleared"); }
    catch { onPatch?.({ edi_review_flag: prev }); toast.error("Couldn't set flag"); }
  };
  const amber = flag === "abnormal" || flag === "additional_review";
  const col = amber ? "#9A5A00" : flag === "normal" ? T.sub : T.sub;
  const icon = flag === "normal"
    ? <Check size={15} strokeWidth={2.2} style={{ color: T.mute }} />
    : <Flag size={15} strokeWidth={1.8} style={{ color: amber ? "#FF9500" : (flag ? col : T.mute), fill: flag === "abnormal" ? "#FF9500" : "none" }} />;
  return (
    <>
      <button type="button" ref={btnRef} data-testid={`pd-flag-${analysisId}`} aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={m ? `Review flag: ${m[1]}` : "Set a review flag"}
        className="pd-ws-flag inline-flex items-center gap-1.5 flex-none rounded-[8px] px-2 h-9 hover:bg-black/[.05]">
        {icon}
        <span className="text-[12.5px] font-semibold" style={{ color: flag ? col : T.sub }}>{m ? m[1] : "Flag"}</span>
      </button>
      {open ? (
        <Anchored anchorRef={btnRef} width={210} onClose={() => setOpen(false)}>
          <div style={{ padding: 6 }}>
            {REVIEW_FLAGS.map(([v, label, c, bg, bd]) => (
              <button key={v} type="button" data-testid={`pd-flag-opt-${v}-${analysisId}`} onClick={(e) => choose(v, e)}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-[8px] text-[13px] hover:bg-black/[.04]" style={{ color: T.ink }}>
                <span className="inline-flex items-center justify-center rounded-full" style={{ width: 16, height: 16, background: bg, border: `1px solid ${bd}` }}><Flag size={9} style={{ color: c, fill: c }} /></span>
                <span className="flex-1 text-left">{label}</span>
                {flag === v ? <Check size={14} style={{ color: T.indigo }} /> : null}
              </button>
            ))}
            {flag ? <button type="button" data-testid={`pd-flag-clear-${analysisId}`} onClick={(e) => choose(null, e)}
              className="w-full flex items-center gap-2 px-2.5 py-2 rounded-[8px] text-[13px] hover:bg-black/[.04]" style={{ color: T.sub, borderTop: `1px solid ${T.soft}`, marginTop: 4 }}>
              <X size={13} style={{ color: T.mute }} /><span className="flex-1 text-left">Clear flag</span></button> : null}
          </div>
        </Anchored>
      ) : null}
    </>
  );
}

/* ─── Indicator-lane icons (§2): eye · flag · comment in fixed 22px slots ─── */
// Watchlist eye — outline at rest, filled indigo pupil when on. Firing onMonitor
// on turn-on triggers the one-time "Monitoring" wash the row paints.
export function WatchEye({ analysisId, on, onPatch, onMonitor }) {
  const [busy, setBusy] = useState(false);
  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return; setBusy(true);
    const next = !on;
    onPatch?.({ edi_watchlist: next, edi_watchlist_at: new Date().toISOString() }); // optimistic
    if (next) onMonitor?.();
    try { await setWatchlist(analysisId, next); toast.message(next ? "Monitoring · added to the practice watchlist" : "Removed from the watchlist"); }
    catch { onPatch?.({ edi_watchlist: on }); toast.error("Couldn't update watchlist"); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={toggle} data-testid={`pd-watch-${analysisId}`} aria-pressed={!!on}
      title={on ? "On the practice watchlist" : "Add to the practice watchlist"}
      className={`pd-lane-btn${on ? " on" : ""}`} style={on ? { color: "#5856D6" } : undefined}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
        <path d="M1.5 8s2.4-4.5 6.5-4.5S14.5 8 14.5 8s-2.4 4.5-6.5 4.5S1.5 8 1.5 8z" />
        <circle cx="8" cy="8" r="2.1" fill={on ? "#5856D6" : "none"} />
      </svg>
    </button>
  );
}

// Lane flag — icon only (no pill). Abnormal = filled amber, Additional review =
// amber outline, Normal = grey check. Opens a small menu under the icon.
export function LaneFlag({ analysisId, flag, onPatch }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const m = flagMeta(flag);
  const choose = async (v, e) => {
    e.stopPropagation(); setOpen(false);
    const prev = flag;
    onPatch?.({ edi_review_flag: v, edi_review_flag_at: new Date().toISOString() }); // optimistic
    try { await setReviewFlag(analysisId, v); toast.message(v ? `Flagged ${flagMeta(v)[1]}` : "Flag cleared"); }
    catch { onPatch?.({ edi_review_flag: prev }); toast.error("Couldn't set flag"); }
  };
  const col = flag === "abnormal" || flag === "additional_review" ? "#FF9500" : flag === "normal" ? T.mute : "currentColor";
  return (
    <>
      <button type="button" ref={btnRef} data-testid={`pd-flag-${analysisId}`} aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={m ? `Review flag: ${m[1]}` : "Set a review flag"}
        className={`pd-lane-btn${flag ? " on" : ""}`} style={flag ? { color: col } : undefined}>
        {flag === "normal"
          ? <Check size={15} strokeWidth={2.2} />
          : <Flag size={15} strokeWidth={1.8} style={{ fill: flag === "abnormal" ? "#FF9500" : "none" }} />}
      </button>
      {open ? (
        <Anchored anchorRef={btnRef} width={210} onClose={() => setOpen(false)}>
          <div style={{ padding: 6 }}>
            {REVIEW_FLAGS.map(([v, label, c, bg, bd]) => (
              <button key={v} type="button" data-testid={`pd-flag-opt-${v}-${analysisId}`} onClick={(e) => choose(v, e)}
                className="w-full flex items-center gap-2 px-2.5 py-2 rounded-[8px] text-[13px] hover:bg-black/[.04]" style={{ color: T.ink }}>
                <span className="inline-flex items-center justify-center rounded-full" style={{ width: 16, height: 16, background: bg, border: `1px solid ${bd}` }}><Flag size={9} style={{ color: c, fill: c }} /></span>
                <span className="flex-1 text-left">{label}</span>
                {flag === v ? <Check size={14} style={{ color: T.indigo }} /> : null}
              </button>
            ))}
            {flag ? <button type="button" data-testid={`pd-flag-clear-${analysisId}`} onClick={(e) => choose(null, e)}
              className="w-full flex items-center gap-2 px-2.5 py-2 rounded-[8px] text-[13px] hover:bg-black/[.04]" style={{ color: T.sub, borderTop: `1px solid ${T.soft}`, marginTop: 4 }}>
              <X size={13} style={{ color: T.mute }} /><span className="flex-1 text-left">Clear flag</span></button> : null}
          </div>
        </Anchored>
      ) : null}
    </>
  );
}

// Indicator lane for a list row: eye · flag · comment, in fixed 22px slots.
// When onComments is provided, the comment icon toggles the inline review row (§2).
export function RowReview({ row, onPatch, commentsOpen, onComments, onMonitor }) {
  const n = row.edi_comment_count || 0;
  return (
    <span className="pd-lane" onClick={(e) => e.stopPropagation()} data-testid={`pd-review-${row.id}`}>
      <WatchEye analysisId={row.id} on={!!row.edi_watchlist} onPatch={onPatch} onMonitor={onMonitor} />
      <LaneFlag analysisId={row.id} flag={row.edi_review_flag || null} onPatch={onPatch} />
      {onComments ? (
        <button type="button" data-testid={`pd-comments-btn-${row.id}`} aria-pressed={!!commentsOpen} onClick={(e) => { e.stopPropagation(); onComments(); }}
          title={n ? `${n} comment${n === 1 ? "" : "s"}` : "Add a comment"}
          className={`pd-lane-btn${commentsOpen || n ? " on" : ""}`} style={commentsOpen || n ? { color: "#5856D6" } : undefined}>
          <MessageSquare size={15} strokeWidth={1.9} />{n ? <span className="pd-lane-cnt">{n}</span> : null}
        </button>
      ) : <CommentButton analysisId={row.id} count={n} onPatch={onPatch} />}
    </span>
  );
}

const chartActLabel = (a) => {
  const who = a.by?.name || a.by || a.user || a.by_email || "";
  const act = String(a.action || a.event || a.kind || "").toLowerCase();
  const base = act.includes("add") ? "Chart data added" : act.includes("remov") ? "Chart data removed" : act.includes("retr") ? "Chart data retried" : (act.includes("none") || act.includes("nothing")) ? "Nothing new to add" : (a.label || "Chart update");
  return who ? `${base} · ${who}` : base;
};

function HistoryTab({ row }) {
  const [acts, setActs] = useState(null);
  useEffect(() => { let a = true; chartActivity(row.id).then((d) => { if (a) setActs(d.activity || d.chart_activity || []); }).catch(() => { if (a) setActs([]); }); return () => { a = false; }; }, [row.id]);
  const ev = [];
  ev.push({ what: `Uploaded${row.edi_split_parent_id ? " · from a multi-part upload" : ""}`, at: row.created_at });
  if (row.edi_reviewed_at) ev.push({ what: `${row.edi_reviewed_by || "Someone"} opened it first`, at: row.edi_reviewed_at });
  if (row.edi_review_flag && row.edi_review_flag_at) ev.push({ what: `${row.edi_review_flag_by || "Someone"} flagged it ${flagMeta(row.edi_review_flag)?.[1] || row.edi_review_flag}`, at: row.edi_review_flag_at });
  if (row.edi_watchlist && row.edi_watchlist_at) ev.push({ what: `${row.edi_watchlist_by || "Someone"} added it to the practice watchlist`, at: row.edi_watchlist_at });
  (acts || []).forEach((a) => ev.push({ what: chartActLabel(a), detail: a.detail || a.note, at: a.at || a.created_at }));
  ev.sort((x, y) => String(y.at || "").localeCompare(String(x.at || "")));
  return (
    <div className="pd-rr-hist" data-testid={`pd-rr-history-${row.id}`}>
      {acts === null ? <div className="pd-rr-empty">Loading…</div>
        : ev.length === 0 ? <div className="pd-rr-empty">No history yet.</div>
          : ev.map((e, i) => (
            <div className="pd-rr-ev" key={i}>
              <span className="pd-rr-evi"><History size={12} /></span>
              <span className="pd-rr-evt">{e.what}{e.detail ? <span className="pd-rr-evd">{e.detail}</span> : null}</span>
              <span className="pd-rr-evwhen">{whenStr(e.at)}</span>
            </div>))}
      <div className="pd-rr-foot">Flag and watchlist show their latest change. Comments are on the Comments tab.</div>
    </div>
  );
}

// Inline review row (§2): expands directly under a list row with Comments / History tabs.
export function ReviewRow({ row, onPatch, onClose, onOpenDoc }) {
  const [tab, setTab] = useState("comments");
  const n = row.edi_comment_count || 0;
  return (
    <div className="pd-reviewrow pd-scope" data-testid={`pd-reviewrow-${row.id}`} onClick={(e) => e.stopPropagation()}>
      <div className="pd-rr-inner">
        <div className="pd-rr-head">
          <button type="button" className={`pd-rr-tab ${tab === "comments" ? "on" : ""}`} onClick={() => setTab("comments")} data-testid={`pd-rr-tab-comments-${row.id}`}>Comments {n}</button>
          <button type="button" className={`pd-rr-tab ${tab === "history" ? "on" : ""}`} onClick={() => setTab("history")} data-testid={`pd-rr-tab-history-${row.id}`}>History</button>
          <span className="flex-1" />
          {onOpenDoc ? <button type="button" className="pd-rr-open" onClick={onOpenDoc} data-testid={`pd-rr-open-${row.id}`}>Open document →</button> : null}
          <button type="button" className="pd-rr-x" onClick={onClose} aria-label="Close" data-testid={`pd-rr-close-${row.id}`}><X size={14} /></button>
        </div>
        <div className="pd-rr-body">
          {tab === "comments" ? <CommentsThread analysisId={row.id} onPatch={onPatch} /> : <HistoryTab row={row} />}
        </div>
      </div>
    </div>
  );
}
